export const MAX_NUMERIC_DIGITS = 8;

const NON_DIGITS = /\D/g;
const THOUSANDS_SEPARATOR_POSITIONS = /\B(?=(\d{3})+(?!\d))/g;

/**
 * @param {string} digits - Digits only, without separators.
 * @returns {string} The digits grouped in thousands, e.g. '10000000' -> '10,000,000'.
 */
export function formatWithCommas(digits) {
  return digits.replace(THOUSANDS_SEPARATOR_POSITIONS, ',');
}

/**
 * @param {string} text - Any text; only its digits are read (commas, currency symbols and any
 *   other characters are ignored).
 * @returns {number|null} The numeric value, or null when the text has no digits (an empty field).
 */
export function parseDigits(text) {
  const digits = (text ?? '').replace(NON_DIGITS, '');
  return digits ? Number(digits) : null;
}

/**
 * Keeps a text input numeric while the user types or pastes: drops every non-digit, limits the
 * length, drops leading zeros and groups the digits in thousands. The caret stays after the same
 * digit it was after, so editing in the middle of a value doesn't throw it to the end.
 *
 * @param {HTMLInputElement} input
 * @param {number} [maxDigits]
 * @returns {string} The resulting digits, without separators ('' when the field is empty).
 */
export function formatNumericInput(input, maxDigits = MAX_NUMERIC_DIGITS) {
  const caret = input.selectionStart ?? input.value.length;
  const digitsBeforeCaret = input.value.slice(0, caret).replace(NON_DIGITS, '').length;

  const digits = input.value.replace(NON_DIGITS, '').slice(0, maxDigits).replace(/^0+(?=\d)/, '');
  const formatted = formatWithCommas(digits);
  input.value = formatted;

  let position = 0;
  let digitsSeen = 0;
  const targetDigits = Math.min(digitsBeforeCaret, digits.length);
  while (position < formatted.length && digitsSeen < targetDigits) {
    if (formatted[position] !== ',') {
      digitsSeen += 1;
    }
    position += 1;
  }
  input.setSelectionRange(position, position);

  return digits;
}

/**
 * A Min/Max pair conflicts when both are set and the minimum is greater than the maximum. Per the
 * Price and Size Confluence specs, resolving a conflict always clears the Maximum (regardless of
 * which of the two fields the user edited last) and keeps the Minimum.
 *
 * @param {Object} params
 * @param {number|null} params.min
 * @param {number|null} params.max
 * @returns {boolean}
 */
export function hasMinMaxConflict({ min, max }) {
  return min !== null && max !== null && min > max;
}
